import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import type * as React from 'react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
	"inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
	{
		variants: {
			variant: {
				default: 'bg-primary text-primary-foreground hover:bg-primary/90',
				destructive:
					'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
				outline:
					'border bg-background shadow-xs hover:bg-accent/10 hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
				secondary:
					'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				ghost:
					'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
				link: 'text-primary underline-offset-4 hover:underline',
				cancel:
					'border bg-background shadow-xs hover:bg-kaefer-light-silver hover:text-kaefer-dark-grey dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
				primary:
					'bg-kaefer-honey-yellow text-kaefer-dark-grey hover:bg-kaefer-sunny-yellow',
				'kaefer-grey':
					'bg-kaefer-dark-grey text-white hover:bg-kaefer-medium-grey w-full',
				'card-select':
					'rounded-lg border-2 border-kaefer-light-silver bg-white text-kaefer-dark-grey hover:bg-muted/50 dark:bg-background',
				'close-dark':
					'rounded-full bg-kaefer-dark-grey text-white shadow-md hover:bg-kaefer-red',
				'close-light':
					'rounded-full bg-white text-kaefer-dark-grey shadow-md hover:bg-kaefer-light-silver',
				'password-icon':
					'shrink-0 text-kaefer-silver hover:text-kaefer-dark-grey hover:bg-transparent',
				'potential-low':
					'border-2 border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-700',
				'potential-medium':
					'border-2 border-amber-400 bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-700',
				'potential-high':
					'border-2 border-red-400 bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300 dark:border-red-700',
				'potential-n1':
					'border-2 border-rose-600 bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300 dark:border-rose-600',
				'potential-n2':
					'border-2 border-orange-500 bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-500',
				'potential-n3':
					'border-2 border-yellow-500 bg-yellow-50 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300 dark:border-yellow-500',
				'potential-n4':
					'border-2 border-teal-600 bg-teal-50 text-teal-700 dark:bg-teal-950 dark:text-teal-300 dark:border-teal-600',
				'potential-n5':
					'border-2 border-teal-700 bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-200 dark:border-teal-700',
			},
			size: {
				// mobile-first taller buttons; shrink slightly on larger screens
				default: 'h-13 px-4 py-2 has-[>svg]:px-3 sm:h-9 w-full',
				xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
				sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
				lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
				icon: 'size-9',
				'icon-xs': "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
				'icon-sm': 'size-8',
				'icon-lg': 'size-10',
				'icon-circle-sm':
					"size-6 rounded-full p-1 [&_svg:not([class*='size-'])]:size-3.5",
				'icon-circle': 'size-8 rounded-full p-1',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
)

function Button({
	className,
	variant = 'default',
	size = 'default',
	asChild = false,
	...props
}: React.ComponentProps<'button'> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean
	}) {
	const Comp = asChild ? Slot.Root : 'button'

	return (
		<Comp
			data-slot="button"
			data-variant={variant}
			data-size={size}
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		/>
	)
}

export { Button, buttonVariants }
